<template>
  <div>
    <!-- Full-screen loading overlay -->
    <v-overlay v-model="pageLoading" class="align-center justify-center" persistent>
      <div class="text-center">
        <v-progress-circular
          indeterminate
          size="64"
          color="primary"
        ></v-progress-circular>
        <div class="mt-4 text-h6">Đang tải dữ liệu...</div>
      </div>
    </v-overlay>

    <!-- Traffic Source Statistics -->
    <v-row class="mb-6">
      <v-col cols="12">
        <v-card elevation="2">
          <v-card-title class="pb-2">
            <v-icon icon="mdi-chart-bar" class="mr-2"></v-icon>
            Thống kê nguồn traffic
          </v-card-title>
          <v-card-text>
            <div v-if="trafficStats && trafficStats.by_source && trafficStats.by_source.length > 0">
              <!-- Summary Cards -->
              <v-row class="mb-4">
                <v-col cols="12" sm="6" md="3">
                  <v-card class="summary-card" variant="tonal" color="primary">
                    <v-card-text class="pa-4 text-center">
                      <v-icon size="32" color="primary" class="mb-2">mdi-cart</v-icon>
                      <div class="text-h6 font-weight-bold">{{ trafficStats.totals.total_orders }}</div>
                      <div class="text-caption">Tổng đơn hàng</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <v-card class="summary-card" variant="tonal" color="success">
                    <v-card-text class="pa-4 text-center">
                      <v-icon size="32" color="success" class="mb-2">mdi-check-circle</v-icon>
                      <div class="text-h6 font-weight-bold">{{ trafficStats.totals.paid_orders }}</div>
                      <div class="text-caption">Đã thanh toán</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <v-card class="summary-card" variant="tonal" color="warning">
                    <v-card-text class="pa-4 text-center">
                      <v-icon size="32" color="warning" class="mb-2">mdi-clock</v-icon>
                      <div class="text-h6 font-weight-bold">{{ trafficStats.totals.pending_orders }}</div>
                      <div class="text-caption">Chờ thanh toán</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <v-card class="summary-card" variant="tonal" color="info">
                    <v-card-text class="pa-4 text-center">
                      <v-icon size="32" color="info" class="mb-2">mdi-percent</v-icon>
                      <div class="text-h6 font-weight-bold">{{ trafficStats.totals.conversion_rate }}%</div>
                      <div class="text-caption">Tỷ lệ chuyển đổi</div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Traffic Source Bar Chart -->
              <div class="traffic-chart-container">
                <h4 class="mb-4">Phân tích theo nguồn traffic</h4>
                <div class="traffic-bars">
                  <div 
                    v-for="source in trafficStats.by_source" 
                    :key="source.source"
                    class="traffic-bar-row mb-3"
                  >
                    <div class="d-flex justify-space-between align-center mb-1">
                      <div class="d-flex align-center">
                        <v-chip :color="getSourceColor(source.source)" variant="tonal" size="small" class="mr-2">
                          {{ source.source }}
                        </v-chip>
                        <span class="text-body-2">{{ source.conversion_rate }}%</span>
                      </div>
                      <div class="text-body-2 font-weight-bold">
                        {{ source.paid_orders }}/{{ source.total_orders }}
                      </div>
                    </div>
                    <div class="progress-container">
                      <div class="progress-bg">
                        <div 
                          class="progress-fill"
                          :style="`width: ${source.conversion_rate}%; background-color: ${getSourceColorCode(source.source)}`"
                        ></div>
                      </div>
                    </div>
                    <!-- Revenue info on mobile -->
                    <div class="d-flex justify-space-between align-center mt-1" v-if="$vuetify.display.mobile">
                      <span class="text-caption">Doanh thu:</span>
                      <span class="text-caption font-weight-bold text-success">{{ formatCurrency(source.total_revenue) }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Detailed Stats Table (Desktop) -->
              <div v-if="!$vuetify.display.mobile" class="mt-6">
                <h4 class="mb-3">Chi tiết theo nguồn</h4>
                <v-table class="elevation-1">
                  <thead>
                    <tr>
                      <th>Nguồn</th>
                      <th>Tổng đơn</th>
                      <th>Đã TT</th>
                      <th>Chờ TT</th>
                      <th>Tỷ lệ CV</th>
                      <th>Doanh thu</th>
                      <th>AOV</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="source in trafficStats.by_source" :key="source.source">
                      <td>
                        <v-chip :color="getSourceColor(source.source)" variant="tonal" size="small">
                          {{ source.source }}
                        </v-chip>
                      </td>
                      <td class="font-weight-bold">{{ source.total_orders }}</td>
                      <td class="text-success font-weight-bold">{{ source.paid_orders }}</td>
                      <td class="text-warning">{{ source.pending_orders }}</td>
                      <td>
                        <v-chip color="info" variant="tonal" size="small">
                          {{ source.conversion_rate }}%
                        </v-chip>
                      </td>
                      <td class="font-weight-bold text-success">{{ formatCurrency(source.total_revenue) }}</td>
                      <td>{{ formatCurrency(source.avg_order_value) }}</td>
                    </tr>
                  </tbody>
                </v-table>
              </div>
            </div>
            <div v-else class="text-center py-8">
              <v-icon size="64" color="grey">mdi-chart-bar</v-icon>
              <p class="text-grey mt-2">Chưa có dữ liệu thống kê</p>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-card>
      <v-card-title class="text-h6 d-flex align-center justify-space-between">
        <div class="d-flex align-center">
          <v-icon class="mr-2">mdi-cart</v-icon>
          Danh sách đơn hàng
        </div>
        <v-text-field
          v-model="search"
          label="Tìm kiếm đơn hàng"
          @input="onSearchInput"
          clearable
          prepend-inner-icon="mdi-magnify"
          variant="outlined"
          density="compact"
          class="search-input"
          style="max-width: 320px;"
        />
      </v-card-title>
             <v-card-text>
         <!-- Desktop Table View -->
         <div v-if="!$vuetify.display.mobile" class="desktop-view">
           <v-table class="elevation-1">
             <thead>
               <tr>
                 <th @click="sort('id')" style="cursor:pointer">
                   Mã ID
                   <v-icon :style="sortBy === 'id' ? '' : 'opacity:0.3'">
                     {{ sortBy === 'id' ? (sortOrder === 'ASC' ? 'mdi-arrow-up' : 'mdi-arrow-down') : 'mdi-arrow-up' }}
                   </v-icon>
                 </th>
                 <th @click="sort('product_names')" style="cursor:pointer">
                   Sản phẩm
                   <v-icon :style="sortBy === 'product_names' ? '' : 'opacity:0.3'">
                     {{ sortBy === 'product_names' ? (sortOrder === 'ASC' ? 'mdi-arrow-up' : 'mdi-arrow-down') : 'mdi-arrow-up' }}
                   </v-icon>
                 </th>
                 <th @click="sort('customer_name')" style="cursor:pointer">
                   Khách hàng
                   <v-icon :style="sortBy === 'customer_name' ? '' : 'opacity:0.3'">
                     {{ sortBy === 'customer_name' ? (sortOrder === 'ASC' ? 'mdi-arrow-up' : 'mdi-arrow-down') : 'mdi-arrow-up' }}
                   </v-icon>
                 </th>
                 <th @click="sort('customer_email')" style="cursor:pointer">
                   Email
                   <v-icon :style="sortBy === 'customer_email' ? '' : 'opacity:0.3'">
                     {{ sortBy === 'customer_email' ? (sortOrder === 'ASC' ? 'mdi-arrow-up' : 'mdi-arrow-down') : 'mdi-arrow-up' }}
                   </v-icon>
                 </th>
                 <th @click="sort('customer_phone')" style="cursor:pointer">
                   SĐT
                   <v-icon :style="sortBy === 'customer_phone' ? '' : 'opacity:0.3'">
                     {{ sortBy === 'customer_phone' ? (sortOrder === 'ASC' ? 'mdi-arrow-up' : 'mdi-arrow-down') : 'mdi-arrow-up' }}
                   </v-icon>
                 </th>
                 <th @click="sort('note')" style="cursor:pointer">
                   Ghi chú
                   <v-icon :style="sortBy === 'note' ? '' : 'opacity:0.3'">
                     {{ sortBy === 'note' ? (sortOrder === 'ASC' ? 'mdi-arrow-up' : 'mdi-arrow-down') : 'mdi-arrow-up' }}
                   </v-icon>
                 </th>
                 <th>
                   Nguồn traffic
                 </th>
                 <th @click="sort('created_at')" style="cursor:pointer">
                  Ngày Tạo
                  <v-icon :style="sortBy === 'created_at' ? '' : 'opacity:0.3'">
                    {{ sortBy === 'created_at' ? (sortOrder === 'ASC' ? 'mdi-arrow-up' : 'mdi-arrow-down') : 'mdi-arrow-up' }}
                  </v-icon>
                </th>
                <th @click="sort('status')" style="cursor:pointer">
                  Trạng thái
                  <v-icon :style="sortBy === 'status' ? '' : 'opacity:0.3'">
                    {{ sortBy === 'status' ? (sortOrder === 'ASC' ? 'mdi-arrow-up' : 'mdi-arrow-down') : 'mdi-arrow-up' }}
                  </v-icon>
                </th>
                <th class="text-center">Thao tác</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="order in allOrders" :key="order.id">
                <td>{{ order.id }}</td>
                <td>
                  <div v-if="order.product_names && order.product_names.length > 0">
                    {{ order.product_names.join(', ') }}
                  </div>
                  <div v-else-if="order.product_name">
                    {{ order.product_name }}
                  </div>
                  <div v-else class="text-grey">
                    Chưa có sản phẩm
                  </div>
                </td>
                <td>{{ order.customer_name }}</td>
                <td>{{ order.customer_email }}</td>
                <td>
                  <div v-if="order.customer_phone">
                    {{ order.customer_phone }}
                  </div>
                  <div v-else class="text-grey">
                    -
                  </div>
                </td>
                <td>
                  <div v-if="order.note" class="note-cell">
                    {{ order.note }}
                  </div>
                  <div v-else class="text-grey">
                    -
                  </div>
                </td>
                <td>
                  <div v-if="order.reference" class="reference-cell">
                    <v-chip size="small" color="info" variant="outlined">
                      {{ order.reference }}
                    </v-chip>
                  </div>
                  <div v-else class="text-grey">
                    -
                  </div>
                </td>
                <td>{{ formatDate(order.created_at) }}</td>
                <td>
                  <v-chip :color="order.status === 1 ? 'success' : 'error'" size="small">
                    {{ order.status === 1 ? 'Đã thanh toán' : 'Chưa thanh toán' }}
                  </v-chip>
                </td>
                <td class="text-center">
                  <v-btn v-if="order.status === 0" color="primary" size="small" @click="pushOrder(order)">
                    <v-icon>mdi-send</v-icon> Đẩy đơn
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </v-table>
          <v-pagination
            v-model="page"
            :length="totalPages"
            @update:modelValue="onPageChange"
            class="custom-pagination"
          />
         </div>

         <!-- Mobile Card View -->
         <div v-else class="mobile-view">
           <div class="mobile-orders-container" ref="ordersContainer">
             <v-card 
               v-for="order in allOrders" 
               :key="order.id" 
               class="mb-3 order-card"
               elevation="2">
               <v-card-text>
                 <div class="d-flex justify-space-between align-center mb-2">
                   <v-chip color="primary" size="small" variant="outlined">
                     ID: {{ order.id }}
                   </v-chip>
                   <v-chip :color="order.status === 1 ? 'success' : 'error'" size="small">
                     {{ order.status === 1 ? 'Đã thanh toán' : 'Chưa thanh toán' }}
                   </v-chip>
                 </div>
                 
                 <div class="order-info">
                   <div class="info-row">
                     <v-icon size="16" color="primary" class="mr-2">mdi-package</v-icon>
                     <strong>Sản phẩm:</strong>
                     <div class="ml-2">
                       <div v-if="order.product_names && order.product_names.length > 0">
                         {{ order.product_names.join(', ') }}
                       </div>
                       <div v-else-if="order.product_name">
                         {{ order.product_name }}
                       </div>
                       <div v-else class="text-grey">
                         Chưa có sản phẩm
                       </div>
                     </div>
                   </div>
                   
                   <div class="info-row">
                     <v-icon size="16" color="info" class="mr-2">mdi-account</v-icon>
                     <strong>Khách hàng:</strong>
                     <span class="ml-2">{{ order.customer_name }}</span>
                   </div>
                   
                   <div class="info-row">
                     <v-icon size="16" color="success" class="mr-2">mdi-email</v-icon>
                     <strong>Email:</strong>
                     <span class="ml-2">{{ order.customer_email }}</span>
                   </div>
                   
                   <div class="info-row" v-if="order.customer_phone">
                     <v-icon size="16" color="warning" class="mr-2">mdi-phone</v-icon>
                     <strong>SĐT:</strong>
                     <span class="ml-2">{{ order.customer_phone }}</span>
                   </div>
                   
                   <div class="info-row" v-if="order.note">
                     <v-icon size="16" color="purple" class="mr-2">mdi-note</v-icon>
                     <strong>Ghi chú:</strong>
                     <span class="ml-2">{{ order.note }}</span>
                   </div>
                   
                   <div class="info-row" v-if="order.reference">
                     <v-icon size="16" color="teal" class="mr-2">mdi-web</v-icon>
                     <strong>Nguồn:</strong>
                     <v-chip size="x-small" color="info" variant="outlined" class="ml-2">
                       {{ order.reference }}
                     </v-chip>
                   </div>
                   
                   <div class="info-row">
                     <v-icon size="16" color="grey" class="mr-2">mdi-calendar</v-icon>
                     <strong>Ngày tạo:</strong>
                     <span class="ml-2">{{ formatDate(order.created_at) }}</span>
                   </div>
                 </div>
                 
                 <div v-if="order.status === 0" class="d-flex justify-center mt-3">
                   <v-btn color="primary" size="small" @click="pushOrder(order)" block>
                     <v-icon class="mr-2">mdi-send</v-icon>
                     Đẩy đơn
                   </v-btn>
                 </div>
               </v-card-text>
             </v-card>
           </div>
           
           <!-- Loading indicator for infinite scroll -->
           <div v-if="loadingMore" class="text-center py-4">
             <v-progress-circular indeterminate color="primary"></v-progress-circular>
             <div class="mt-2">Đang tải thêm...</div>
           </div>
           
           <!-- End of list indicator -->
           <div v-if="hasReachedEnd && allOrders.length > 0" class="text-center py-4 text-grey">
             <v-icon>mdi-check-circle</v-icon>
             <div>Đã hiển thị tất cả đơn hàng</div>
           </div>
         </div>
       </v-card-text>
    </v-card>
    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000" top right>
      {{ snackbarText }}
    </v-snackbar>
  </div>
</template>
<script>
import axios from '@/http';
export default {
  name: 'Order',
  data() {
    return {
      orders: [],
      allOrders: [], // For infinite scroll
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 1,
      search: '',
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
      sortBy: 'created_at',
      sortOrder: 'DESC',
      pageLoading: false,
      loadingMore: false, // For infinite scroll loading
      hasReachedEnd: false, // Track if we've loaded all data
      currentMobilePage: 1, // Separate page tracking for mobile
      trafficStats: null, // Traffic source statistics
    }
  },
  methods: {
    async fetchOrders(page = this.page, limit = this.limit, search = this.search, sortBy = this.sortBy, sortOrder = this.sortOrder, showPageLoading = false, append = false) {
      if (showPageLoading) {
        this.pageLoading = true;
      }
      if (append) {
        this.loadingMore = true;
      }
      
      try {
        const res = await axios.get('/orders', {
          params: { page, limit, search, sort_by: sortBy, sort_order: sortOrder }
        });
        
        if (append) {
          // For mobile infinite scroll
          this.allOrders = [...this.allOrders, ...res.data.data];
          this.hasReachedEnd = res.data.data.length < limit || page >= res.data.totalPages;
        } else {
          // For desktop pagination or initial load
          this.orders = res.data.data;
          this.allOrders = res.data.data;
        }
        
        this.total = res.data.total;
        this.page = res.data.page;
        this.totalPages = res.data.totalPages;
      } catch (e) {
        this.snackbarText = 'Lấy danh sách đơn hàng thất bại!';
        this.snackbarColor = 'error';
        this.snackbar = true;
      } finally {
        if (showPageLoading) {
          this.pageLoading = false;
        }
        if (append) {
          this.loadingMore = false;
        }
      }
    },

    async loadMoreOrders() {
      if (this.loadingMore || this.hasReachedEnd) return;
      
      this.currentMobilePage++;
      await this.fetchOrders(
        this.currentMobilePage, 
        this.limit, 
        this.search, 
        this.sortBy, 
        this.sortOrder, 
        false, 
        true // append = true
      );
    },

    resetMobileData() {
      this.allOrders = [];
      this.currentMobilePage = 1;
      this.hasReachedEnd = false;
    },
    sort(column) {
      if (this.sortBy === column) {
        this.sortOrder = this.sortOrder === 'ASC' ? 'DESC' : 'ASC';
      } else {
        this.sortBy = column;
        this.sortOrder = 'ASC';
      }
      this.page = 1;
      this.resetMobileData();
      this.fetchOrders(1, this.limit, this.search, this.sortBy, this.sortOrder);
    },
    onSearchInput() {
      clearTimeout(this._searchTimeout);
      this._searchTimeout = setTimeout(() => {
        this.page = 1;
        this.resetMobileData();
        this.fetchOrders(1, this.limit, this.search, this.sortBy, this.sortOrder);
      }, 400);
    },
    onPageChange(newPage) {
      this.page = newPage;
      this.fetchOrders(newPage, this.limit, this.search, this.sortBy, this.sortOrder);
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      return d.toLocaleString('vi-VN');
    },
    async pushOrder(order) {
      try {
        await axios.put(`/orders/${order.id}/status`, { status: 1 });
        order.status = 1;
        this.snackbarText = 'Cập nhật trạng thái đơn hàng thành công!';
        this.snackbarColor = 'success';
        this.snackbar = true;
      } catch (e) {
        this.snackbarText = 'Cập nhật trạng thái đơn hàng thất bại!';
        this.snackbarColor = 'error';
        this.snackbar = true;
      }
    },

    setupInfiniteScroll() {
      if (!('IntersectionObserver' in window)) return;
      
      this.$nextTick(() => {
        if (this.$refs.ordersContainer) {
          this.observer = new IntersectionObserver((entries) => {
            const target = entries[0];
            if (target.isIntersecting && this.$vuetify.display.mobile) {
              this.loadMoreOrders();
            }
          }, {
            root: null,
            rootMargin: '200px',
            threshold: 0.1
          });
          
          // Create a sentinel element at the bottom
          const sentinel = document.createElement('div');
          sentinel.style.height = '1px';
          sentinel.style.background = 'transparent';
          this.$refs.ordersContainer?.appendChild(sentinel);
          this.observer.observe(sentinel);
        }
      });
    },

    async fetchTrafficStats() {
      try {
        const response = await axios.get('/orders/stats/traffic-sources');
        this.trafficStats = response.data.data;
      } catch (error) {
        console.error('Error fetching traffic stats:', error);
        this.snackbarText = 'Lỗi khi tải thống kê nguồn traffic';
        this.snackbarColor = 'error';
        this.snackbar = true;
      }
    },

    getSourceColor(source) {
      const colorMap = {
        'facebook': 'blue',
        'google': 'orange', 
        'tiktok': 'pink',
        'instagram': 'purple',
        'youtube': 'red',
        'twitter': 'light-blue',
        'linkedin': 'indigo',
        'direct': 'green',
        'email': 'teal',
        'referral': 'amber'
      };
      return colorMap[source?.toLowerCase()] || 'grey';
    },

    getSourceColorCode(source) {
      const colorCodeMap = {
        'facebook': '#1976D2',
        'google': '#FF9800', 
        'tiktok': '#E91E63',
        'instagram': '#9C27B0',
        'youtube': '#F44336',
        'twitter': '#03A9F4',
        'linkedin': '#3F51B5',
        'direct': '#4CAF50',
        'email': '#009688',
        'referral': '#FFC107'
      };
      return colorCodeMap[source?.toLowerCase()] || '#9E9E9E';
    },

    formatCurrency(amount) {
      if (!amount) return '0 VND';
      return new Intl.NumberFormat('vi-VN', {
        style: 'currency',
        currency: 'VND'
      }).format(amount);
    }
  },
  mounted() {
    this.fetchOrders(this.page, this.limit, this.search, this.sortBy, this.sortOrder, true); // Show page loading on initial load
    this.fetchTrafficStats(); // Load traffic statistics
    this.setupInfiniteScroll();
  },
  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}
</script>
<style scoped>
.search-input {
  max-width: 320px;
  margin-bottom: 0;
}

.text-grey {
  color: #999;
  font-style: italic;
}

 .note-cell {
   max-width: 200px;
   word-wrap: break-word;
   white-space: pre-wrap;
 }
 
 .reference-cell {
   max-width: 150px;
   word-wrap: break-word;
 }

/* Traffic Stats Styles */
.summary-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border-radius: 12px !important;
}

.summary-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.summary-card .text-h6 {
  font-size: 1.5rem !important;
  line-height: 1.2 !important;
}

.summary-card .text-caption {
  font-size: 0.85rem !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.8;
}

.traffic-chart-container {
  margin-top: 16px;
}

.traffic-bars {
  max-width: 100%;
}

.traffic-bar-row {
  background: rgba(var(--v-theme-surface), 0.8);
  border-radius: 8px;
  padding: 12px;
  border: 1px solid rgba(var(--v-border-color), 0.12);
}

.progress-container {
  width: 100%;
  margin: 8px 0;
}

.progress-bg {
  width: 100%;
  height: 20px;
  background-color: rgba(var(--v-theme-surface-variant), 0.3);
  border-radius: 10px;
  overflow: hidden;
  position: relative;
}

.progress-fill {
  height: 100%;
  border-radius: 10px;
  transition: width 0.8s ease;
  background: linear-gradient(45deg, 
    rgba(255, 255, 255, 0.1) 25%, 
    transparent 25%, 
    transparent 50%, 
    rgba(255, 255, 255, 0.1) 50%, 
    rgba(255, 255, 255, 0.1) 75%, 
    transparent 75%, 
    transparent
  );
  background-size: 20px 20px;
  animation: progress-animation 1s linear infinite;
}

@keyframes progress-animation {
  0% {
    background-position: 0 0;
  }
  100% {
    background-position: 20px 0;
  }
}

/* Mobile Card Styles */
.mobile-view {
  padding: 0;
}

.mobile-orders-container {
  padding: 8px 0;
}

.order-card {
  border-radius: 12px !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.order-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.order-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.info-row {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  min-height: 24px;
  font-size: 14px;
}

.info-row strong {
  min-width: 70px;
  font-weight: 600;
}

.info-row .ml-2 {
  flex: 1;
  word-break: break-word;
}

/* Desktop responsive adjustments */
@media (max-width: 1200px) {
  .desktop-view .v-table thead th {
    font-size: 13px;
    padding: 8px 4px;
  }
  
  .desktop-view .v-table tbody td {
    font-size: 13px;
    padding: 8px 4px;
  }
  
  .note-cell,
  .reference-cell {
    max-width: 120px;
  }
}

/* Mobile specific styles */
@media (max-width: 768px) {
  .search-input {
    max-width: 100% !important;
    margin-bottom: 16px;
  }
  
  .v-card-title {
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 16px;
  }
  
  .mobile-orders-container {
    padding: 4px 0;
  }
  
  .order-card {
    margin-bottom: 12px !important;
  }
  
  .info-row {
    font-size: 13px;
  }
  
  .info-row strong {
    min-width: 65px;
    font-size: 13px;
  }

  /* Traffic stats mobile adjustments */
  .summary-card .text-h6 {
    font-size: 1.25rem !important;
  }
  
  .summary-card .text-caption {
    font-size: 0.75rem !important;
  }
  
  .traffic-bar-row {
    padding: 8px;
    margin-bottom: 8px !important;
  }
  
  .progress-bg {
    height: 16px;
  }
  
  .traffic-chart-container h4 {
    font-size: 1.1rem;
    margin-bottom: 12px !important;
  }
}
</style>